import React from 'react';
import {
  Check,
  CheckSquare,
  ExternalLink,
  Info,
  Loader2,
  Upload,
} from 'lucide-react';
import Card from '../../components/Card';
import { FormInput, FormSelect } from '../../components/FormComponents';
import { apiUrl } from '../../config/api';
import { FEE_FREQUENCY_OPTIONS, optionLabel } from '../../constants/contracts';
import {
  LongText,
  Subhead,
  TimelineItem,
} from './RequestDetailPrimitives';

// Right-rail "Properties" panel — a kv-list of facts that aren't already
// shown in the header card's metrics strip. Uses the standard Card
// title pattern so the heading matches every other card on the page.
function RequestSnapshot({ request, formatDate }) {
  return (
    <Card title="Properties" icon={Info}>
      <dl className="kv-list">
        <dt>Requester</dt>
        <dd>{request.requesterName || request.requesterEmail || <span className="muted">—</span>}</dd>

        {request.vendorContactName && (
          <>
            <dt>Contact</dt>
            <dd>{request.vendorContactName}</dd>
          </>
        )}
        {request.vendorEmail && (
          <>
            <dt>Vendor email</dt>
            <dd className="mono" style={{ fontSize: 12 }}>{request.vendorEmail}</dd>
          </>
        )}
        {request.vendorAddress && (
          <>
            <dt>Address</dt>
            <dd>{request.vendorAddress}</dd>
          </>
        )}
        <dt>Documents</dt>
        <dd>{(request.documents || []).length} file{(request.documents || []).length === 1 ? '' : 's'}</dd>
        <dt>Created</dt>
        <dd className="mono" style={{ fontSize: 12 }}>{formatDate(request.createdAt)}</dd>
        {request.updatedAt && request.updatedAt !== request.createdAt && (
          <>
            <dt>Updated</dt>
            <dd className="mono" style={{ fontSize: 12 }}>{formatDate(request.updatedAt)}</dd>
          </>
        )}
      </dl>
      {request.vendorWebsite && (
        <a
          href={request.vendorWebsite.startsWith('http') ? request.vendorWebsite : `https://${request.vendorWebsite}`}
          target="_blank"
          rel="noreferrer"
          className="btn outline sm"
          style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Vendor site
        </a>
      )}
    </Card>
  );
}

// Rail "Progress" card — the request's required path as a vertical stepper
// with dates and actors. The AI first-pass is deliberately not a step.
function ProgressCard({ request, formatDate }) {
  const status = request.status;
  const lanesApproved =
    (request.legalReviewStatus === 'approved' ? 1 : 0) +
    (request.securityReviewStatus === 'approved' ? 1 : 0);
  const terminal = status === 'rejected' || status === 'withdrawn';

  const steps = [
    {
      label: 'Drafted',
      state: 'done',
      detail: [formatDate(request.createdAt), request.requesterName].filter(Boolean).join(' · '),
    },
    {
      label: 'Submitted',
      state: request.submittedAt || status !== 'draft' ? 'done' : 'todo',
      detail: request.submittedAt
        ? [formatDate(request.submittedAt), request.requesterName].filter(Boolean).join(' · ')
        : null,
    },
  ];
  if (terminal) {
    steps.push({
      label: status === 'rejected' ? 'Rejected in review' : 'Withdrawn',
      state: 'terminal',
      tone: status === 'rejected' ? 'var(--color-red-500)' : 'var(--text-tertiary)',
      detail: request.reviewedAt ? formatDate(request.reviewedAt) : null,
    });
  } else {
    steps.push({
      label: 'Legal & Security sign-off',
      state: lanesApproved === 2 ? 'done'
        : ['submitted', 'under_review', 'changes_requested'].includes(status) ? 'active' : 'todo',
      detail: status === 'changes_requested'
        ? 'Changes requested'
        : lanesApproved === 2 ? null : `In progress · ${lanesApproved} of 2`,
    });
    steps.push({
      label: 'Approved',
      state: status === 'approved' || request.contractId ? 'done' : 'todo',
    });
    steps.push({
      label: 'Contract created',
      state: request.contractId ? 'done' : status === 'approved' ? 'active' : 'todo',
    });
  }

  return (
    <Card title="Progress" icon={CheckSquare}>
      <div>
        {steps.map((step, i) => {
          const last = i === steps.length - 1;
          const circle =
            step.state === 'done' ? (
              <span style={{
                width: 18, height: 18, borderRadius: 999, background: 'var(--color-green-500)',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Check className="w-3 h-3" />
              </span>
            ) : step.state === 'active' ? (
              <span style={{
                width: 18, height: 18, borderRadius: 999, flexShrink: 0,
                border: '2px solid var(--color-purple-500)',
                background: 'var(--surface-purple)',
              }} />
            ) : step.state === 'terminal' ? (
              <span style={{
                width: 18, height: 18, borderRadius: 999, flexShrink: 0,
                border: `2px solid ${step.tone}`,
              }} />
            ) : (
              <span style={{
                width: 18, height: 18, borderRadius: 999, flexShrink: 0,
                border: '2px solid var(--border-primary)',
              }} />
            );
          return (
            <div key={step.label} className="row" style={{ gap: 12, alignItems: 'stretch' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {circle}
                {!last && (
                  <span style={{
                    width: 2, flex: 1, minHeight: 14,
                    background: step.state === 'done' ? 'var(--color-green-500)' : 'var(--border-primary)',
                  }} />
                )}
              </div>
              <div style={{ paddingBottom: last ? 0 : 16, minWidth: 0 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: step.state === 'active' ? 600 : 500,
                  color:
                    step.state === 'terminal' ? step.tone
                    : step.state === 'active' ? 'var(--text-purple)'
                    : step.state === 'done' ? 'var(--text-primary)'
                    : 'var(--text-tertiary)',
                }}>
                  {step.label}
                </div>
                {step.detail && (
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{step.detail}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DocumentsPanel({
  request,
  canUploadDocs,
  hasElevatedAccess,
  docUpload,
  setDocUpload,
  uploadingDoc,
  handleDocumentUpload,
  formatDateTime,
}) {
  const [showUpload, setShowUpload] = React.useState(false);
  return (
    <Card
      title="Documents"
      icon={Upload}
      action={
        <div className="row" style={{ gap: 8 }}>
          {canUploadDocs && (
            <button
              type="button"
              className="btn outline sm"
              onClick={() => setShowUpload((v) => !v)}
            >
              <Upload className="w-3.5 h-3.5" />
              {showUpload ? 'Cancel' : 'Upload'}
            </button>
          )}
        </div>
      }
    >
      <div className="stack-md">
        <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          Vendor evidence for the reviewers — SOC 2 reports, pen tests, security
          questionnaires. Files are stored in Docket, visible to Legal and
          Security here, and read by the AI first-pass.
        </div>
        {canUploadDocs && showUpload && (
          <div
            className="stack-md"
            style={{
              padding: 12,
              border: '0.5px solid var(--border-primary)',
              borderRadius: 8,
              background: 'var(--surface-alpha-secondary)',
            }}
          >
            {/* Native file input is hidden — clicking the styled
                "Choose file" button forwards to it. Selected filename
                shows next to the button so the user has feedback. */}
            <div>
              <label className="field-label">File</label>
              <input
                id="tprm-doc-upload"
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(e) => setDocUpload((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                style={{ display: 'none' }}
              />
              <div className="row" style={{ gap: 8, alignItems: 'center', minWidth: 0 }}>
                <button
                  type="button"
                  className="btn outline sm"
                  onClick={() => document.getElementById('tprm-doc-upload')?.click()}
                  style={{ flexShrink: 0 }}
                >
                  <Upload className="w-3.5 h-3.5" />
                  Choose file
                </button>
                <span
                  style={{
                    fontSize: 12,
                    color: docUpload.file ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                  }}
                  title={docUpload.file?.name || ''}
                >
                  {docUpload.file?.name || 'No file selected'}
                </span>
              </div>
            </div>
            <FormSelect
              label="Category"
              value={docUpload.category}
              onChange={(e) => setDocUpload((prev) => ({ ...prev, category: e.target.value }))}
              options={[
                { value: 'vendor_doc', label: 'Vendor doc' },
                { value: 'intake', label: 'Intake' },
                // Reviewer artifacts — the backend coerces these to vendor_doc
                // for requesters, so don't offer them.
                ...(hasElevatedAccess
                  ? [
                      { value: 'approval_record', label: 'Approval' },
                      { value: 'ai_report', label: 'AI report' },
                    ]
                  : []),
              ]}
            />
            <FormInput
              label="Document type"
              hint="e.g., SOC 2, MSA"
              value={docUpload.documentType}
              onChange={(e) => setDocUpload((prev) => ({ ...prev, documentType: e.target.value }))}
              placeholder="Optional"
            />
            <button
              onClick={async () => { await handleDocumentUpload(); setShowUpload(false); }}
              disabled={uploadingDoc || !docUpload.file}
              className="btn primary sm"
              style={{ justifyContent: 'center' }}
            >
              {uploadingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingDoc ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        )}
        {request.documents && request.documents.length > 0 ? (
          <div>
            {request.documents.map((doc, i) => (
              <div
                key={doc.id}
                style={{
                  padding: '10px 0',
                  borderTop: i === 0 ? 0 : '0.5px solid var(--border-primary)',
                }}
              >
                <div className="row" style={{ justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="ellipsis" style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>
                      <a
                        href={apiUrl(`/requests/${request.id}/documents/${doc.id}/download`)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {doc.filename}
                      </a>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>
                      {doc.documentType || doc.category} · {formatDateTime(doc.uploadedAt)}
                    </div>
                  </div>
                  <span className="chip gray sq">{doc.category}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
            No documents uploaded.
          </div>
        )}
      </div>
    </Card>
  );
}

// Renderer for the security-relevant submitted data, used as the tail
// section of the Security Review card. Two-column kv-list for short
// answers; long-form answers (data types, retention, access details)
// drop into a dividerless block underneath.
function renderSecurityAssessment(request, formatYesNo) {
  const muted = <span className="muted">—</span>;
  return (
    <>
      <dl
        className="kv-list"
        style={{
          gridTemplateColumns: 'minmax(180px, max-content) 1fr',
          gap: '10px 22px',
        }}
      >
        <dt>NDA</dt>
        <dd>{
          request.ndaStatus === 'yes' ? 'Yes — in place' :
          request.ndaStatus === 'no' ? 'No — not required' :
          request.ndaStatus === 'in_progress' ? 'In progress' :
          muted
        }</dd>
        <dt>Confidential info shared</dt>
        <dd>{formatYesNo(request.confidentialInfoShared) || muted}</dd>
        <dt>Data access level</dt>
        <dd>{
          request.dataAccessLevel === 'none' ? 'None' :
          request.dataAccessLevel === 'limited' ? 'Limited' :
          request.dataAccessLevel === 'full' ? 'Full' :
          muted
        }</dd>
        <dt>System access</dt>
        <dd>{
          request.systemAccess === 'none' ? 'None' :
          request.systemAccess === 'read_only' ? 'Read only' :
          request.systemAccess === 'read_write' ? 'Read / write' :
          muted
        }</dd>
        <dt>AI / ML usage</dt>
        <dd>{formatYesNo(request.aiMlUsage) || muted}</dd>
        <dt>AI training on Lightspark data</dt>
        <dd>{formatYesNo(request.aiTrainingUse) || muted}</dd>
        <dt>AI code / IP generation</dt>
        <dd>{formatYesNo(request.aiCodeOrIpGeneration) || muted}</dd>
        <dt>IP production</dt>
        <dd>{formatYesNo(request.ipProduction) || muted}</dd>
        <dt>Customer interaction</dt>
        <dd>{formatYesNo(request.customerInteraction) || muted}</dd>
        <dt>Related party</dt>
        <dd>{formatYesNo(request.relatedParty) || muted}</dd>
        <dt>Critical to operations</dt>
        <dd>{formatYesNo(request.criticalToOperations) || muted}</dd>
      </dl>

      {(request.dataTypes || request.dataRetention || request.systemAccessDescription) && (
        <div className="stack-md" style={{ marginTop: 14 }}>
          {request.dataTypes && (
            <div>
              <Subhead>Data shared / processed</Subhead>
              <LongText>{request.dataTypes}</LongText>
            </div>
          )}
          {request.dataRetention && (
            <div>
              <Subhead>Retention / deletion</Subhead>
              <LongText>{request.dataRetention}</LongText>
            </div>
          )}
          {request.systemAccessDescription && (
            <div>
              <Subhead>System access details</Subhead>
              <LongText>{request.systemAccessDescription}</LongText>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Renderer for the contract-relevant submitted data — tail section of
// the Legal Review card.
function renderContractDetails(request, formatDate, formatYesNo, formatCurrency) {
  const muted = <span className="muted">—</span>;
  return (
    <>
      <dl
        className="kv-list"
        style={{
          gridTemplateColumns: 'minmax(180px, max-content) 1fr',
          gap: '10px 22px',
        }}
      >
        <dt>Agreement type</dt>
        <dd>{
          request.agreementType === 'services' ? 'Services agreement' :
          request.agreementType === 'saas' ? 'SaaS / subscription' :
          request.agreementType === 'license' ? 'License agreement' :
          request.agreementType === 'consulting' ? 'Consulting agreement' :
          request.agreementType === 'clickwrap' ? 'Click-through / online terms' :
          request.agreementType === 'other' ? request.agreementTypeOther || 'Other' :
          muted
        }</dd>
        <dt>Vendor form / paper</dt>
        <dd>{formatYesNo(request.vendorForm) || muted}</dd>
        <dt>Estimated fees</dt>
        <dd>{formatCurrency(request.estimatedFees) || muted}</dd>
        <dt>Fee frequency</dt>
        <dd>{optionLabel(FEE_FREQUENCY_OPTIONS, request.feeFrequency, muted)}</dd>
        <dt>Start date</dt>
        <dd>{formatDate(request.startDate) || muted}</dd>
        <dt>End date</dt>
        <dd>{formatDate(request.endDate) || muted}</dd>
        <dt>Expense reimbursement</dt>
        <dd>{formatYesNo(request.expenseReimbursement) || muted}</dd>
        <dt>Requires CEO approval</dt>
        <dd>{formatYesNo(request.requiresCeoApproval) || muted}</dd>
        {request.ceoApprovalEvidence && (
          <>
            <dt>CEO approval evidence</dt>
            <dd>{request.ceoApprovalEvidence}</dd>
          </>
        )}
      </dl>
      {request.notes && (
        <div style={{ marginTop: 14 }}>
          <Subhead>Additional notes</Subhead>
          <LongText>{request.notes}</LongText>
        </div>
      )}
    </>
  );
}

export {
  DocumentsPanel,
  ProgressCard,
  LongText,
  RequestSnapshot,
  Subhead,
  TimelineItem,
  renderContractDetails,
  renderSecurityAssessment,
};
